import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { formats, validators } from 'investira.sdk';
import { Stack, Typography, Chip, useMediaQuery } from 'investira.react.components';
import { displays } from 'investira.react.lib';
import { ATIVO_MERCADO, ATIVO_TIPO, PUBLICO_ALVO } from '@investirapri/financa.js/lib/enums';
import { PessoaImage } from '../../molecules';
import { BENCHMARKS, FUNDO_SITUACAO } from '../../../enums';

/**
 * Renderiza o componente SearchAtivosItem.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {*} props.ativo - Propriedade `ativo` do componente.
 * @param {boolean} [props.showAtivoTipo=false] - Usa o tipo como chip principal.
 * @param {*} props.onClick - Propriedade `onClick` do componente.
 *
 * @description
 * Este componente realiza as seguintes operacoes:
 * 1. Recebe as propriedades necessarias para compor a interface
 * 2. Organiza os estados e callbacks usados durante a renderizacao
 * 3. Renderiza a experiencia visual do organismo
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const SearchAtivosItem = memo(props => {
  const { ativo, onClick } = props;
  const isUpMd = useMediaQuery(theme => theme.breakpoints.up('md'));
  const xPrimaryChipLabel = props.showAtivoTipo
    ? ATIVO_TIPO[ativo.ativo_tipo_id]?.descricao
    : ATIVO_MERCADO[ativo.mercado]?.descricao;
  const xFundoSituacao = Object.values(FUNDO_SITUACAO)
    .filter(pItem => pItem.value !== FUNDO_SITUACAO.EM_FUNCIONAMENTO_NORMAL.value)
    .find(pItem => pItem.value === ativo?.situacao);
  const xBenchmark = BENCHMARKS[ativo?.benchmark_indicador_id];
  const xPublicoAlvo = Object.values(PUBLICO_ALVO).find(
    pItem => String(pItem.id) === String(ativo?.publico_alvo)
  );

  return (
    <Stack
      id={ativo.pessoa_id}
      key={ativo.pessoa_id}
      direction="row"
      flexGrow={1}
      spacing={3}
      alignItems="flex-start"
      component="li"
      onClick={onClick}
      sx={{
        px: 2,
        py: 2,
        cursor: 'pointer',
        '&:hover': {
          color: 'gray',
          backgroundColor: 'background.main'
        },
        borderBottom: '1px solid',
        borderBottomColor: 'background.main'
      }}>
      <Stack>
        <PessoaImage
          logoPessoaId={ativo.logo_pessoa_id}
          alt={ativo.pessoa}
          hasLogo={ativo.logo_ok}
          size={36}
        />
      </Stack>
      <Stack flexGrow={1}>
        <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
          {ativo.ticker || ativo.ativo_seuid || ativo.ativo}
        </Typography>
        <Stack direction="row" spacing={{ xs: 0, sm: 1 }} mb={1}>
          <Typography
            variant="caption"
            color="textPrimary"
            sx={{
              display: { xs: 'none', sm: 'inline-block' }
            }}>
            {ativo.ativo || ativo.isin}
          </Typography>
        </Stack>

        <Stack direction="row" sx={{ gap: '8px 8px' }} flexWrap="wrap" spacing={{ xs: 0, sm: 1 }}>
          {!isUpMd && xPrimaryChipLabel && (
            <Chip
              label={xPrimaryChipLabel}
              size="small"
              color="primary"
              sx={{
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              }}
            />
          )}

          {ativo.emissor_cnpj && (
            <Chip label={displays.cnpj(ativo.emissor_cnpj)} size="small" color="secondary" />
          )}

          {ativo.isin && <Chip label={ativo.isin} size="small" color="secondary" />}
          {ativo.serie && <Chip label={`Série: ${ativo.serie}`} size="small" color="secondary" />}
          {ativo.vencimento && (
            <Chip
              label={`Venc.: ${formats.formatDate(ativo.vencimento)}`}
              size="small"
              color="secondary"
            />
          )}
          {ativo?.estruturado && <Chip label="Estruturado" size="small" color="secondary" />}
          {ativo?.qualificado && <Chip label="Qualificado" size="small" color="secondary" />}
          {xPublicoAlvo && <Chip label={xPublicoAlvo.descricao} size="small" color="secondary" />}
          {xBenchmark && <Chip label={xBenchmark.indicador_sigla} size="small" color="secondary" />}
          {ativo?.esg && <Chip label="ESG" size="small" color="secondary" />}
          {ativo?.exclusivo && <Chip label="Exclusivo" size="small" color="secondary" />}
          {validators.isNumber(ativo?.aplicacao_min) && ativo.aplicacao_min > 0 && (
            <Chip
              label={`Aplicação mínima: ${formats.friendlyNumber(ativo.aplicacao_min, 2, true)}`}
              size="small"
              color="secondary"
            />
          )}
          {validators.isNumber(ativo?.pl) && ativo.pl > 0 && (
            <Chip
              label={`Patrimônio liquido: ${formats.friendlyNumber(ativo.pl, 2, true)}`}
              size="small"
              color="secondary"
            />
          )}
          {validators.isNumber(ativo?.cotistas) && ativo.cotistas > 0 && (
            <Chip
              label={`Cotistas: ${formats.friendlyNumber(ativo.cotistas, 2)}`}
              size="small"
              color="secondary"
            />
          )}
          {xFundoSituacao && (
            <Chip
              label={xFundoSituacao.descricao}
              size="small"
              color={xFundoSituacao.color}
              variant="outlined"
            />
          )}
        </Stack>
      </Stack>
      {isUpMd && xPrimaryChipLabel && (
        <Stack minWidth={0} maxWidth="45%">
          <Stack justifyContent="end" maxWidth="100%">
            <Chip
              label={xPrimaryChipLabel}
              size="small"
              color="primary"
              sx={{
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              }}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
});

SearchAtivosItem.propTypes = {
  ativo: PropTypes.object.isRequired,
  showAtivoTipo: PropTypes.bool,
  onClick: PropTypes.func.isRequired
};

SearchAtivosItem.defaultProps = {
  showAtivoTipo: false
};

SearchAtivosItem.displayName = 'SearchAtivosItem';

export default SearchAtivosItem;
